import React, { createContext, useContext, useReducer } from 'react';
import { get, uniqueId } from 'lodash';

export const ApplicationStateContext = createContext({});

const initialState = {
	contacts: {
		list: []
	},
	projects: {
		list: [],
		current: {
			devInfo: { contacts: [], securityMasters: [] },
			prodInfo: { contacts: [], securityMasters: [] },
			deleteModal: false,
			projectCarDetails: {
				subscriptions: {}
			}
		}
	},
	notificationSlideout: {
		show: false
	},
	selectProjectTab:
	{
		activeProdProjectsTab: false,
		activeDevProjectsTab: true
	},
	notificationSettings: {
		projectProgress: true,
		certificateExpiration: true,
		webhookFailure: true
	},
	preferenceSettings: {
		submittedButExpiredCertificate: true,
		successfullyUploadedCertificate: true
	},
	navigation: {
		title: '',
		preventScrollOnRouteChange: false,
	},
	activeChangeRequest: {},
	loading: false,
	error: {
		show: false,
		message: ''
	},
	expiresAt: '',
	loadings: {},
	partnerProjectSettings: {
		filterAppOrPartner: '',
		filterStatus: '',
		filterTechAdvisor: '',
		sortOrder: { colId: 'createdDate', sortBy: -1 },
		pagination: { page: 1, pageSize: 10, skip: 0, totalPages: 1}
	},
	apiDiscovery: {
		intentDropdownSelection: null,
		apiDomainDropDownSelection: [],
		subCategoriesDropDownSelection: [],
		domainDropdownList: [],
		subCategoriesDropdownList: [],
		searchResponse: null,
		isApiContext: true
	},
	toasts: {
		list: []
	},
};

const reducer = (state, action) => {
	switch (action.type) {
		case 'changeTitle':
			return {
				...state,
				navigation: {
					...state.navigation,
					title: action.title
				}
			};
		case 'updatePreventScrollOnRouteChange':
			return {
				...state,
				navigation: {
					...state.navigation,
					preventScrollOnRouteChange: action.preventScrollOnRouteChange
				}
			};
		case 'updateContactsList':
			return {
				...state,
				contacts: { list: action.list }
			};
		case 'updateProjectsList':
			return {
				...state,
				projects: {
					...state.projects,
					list: action.list
				}
			};
		case 'updateCurrentProject':
			return {
				...state,
				projects: {
					...state.projects,
					current: action.project
				}
			};
		case 'updateCurrentProjectDetails':
		return {
			...state,
			projects: {
				...state.projects,
				current: {
					...state.projects.current,
					...action.details
				}
			}
		};
		case 'updateCurrentProjectDevSubscriptions':
			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						projectCarDetails: {
							...state.projects.current.projectCarDetails,
							subscriptions: {
								...state.projects.current.projectCarDetails.subscriptions,
								...action.details
							}
						}
					}
				}
			};
		case 'updateCurrentProjectProdSubscriptions':
			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						projectCarDetails: {
							...state.projects.current.projectCarDetails,
							prod: {
								...state.projects.current.projectCarDetails.prod,
								subscriptions: {
									...(state.projects.current.projectCarDetails.prod ? state.projects.current.projectCarDetails.prod.subscriptions : []),
									...action.details
								}
							}
						}
					}
				}
			};
		case 'updateCurrentDevProjectDetails':
			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						devInfo: {
							...state.projects.current.devInfo,
							...action.details
						}
					}
				}
			};
		case 'updateCurrentProdProjectDetails':
			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						prodInfo: {
							...state.projects.current.prodInfo,
							...action.details
						}
					}
				}
			};
		case 'updateOrgNameInProjectDetailSubscriptions':
			const replaceSubName = (subs = {}) => {
				subs = get(subs, `${action.details.type}.subscriptionList`, []);
				for (let s of subs) {
					if (s.ooid === action.details.ooid) {
						s.organizationName = action.details.organizationName;
					}
				}

			}

			const projectCarDetails = {...(state.projects.current.projectCarDetails)};
			if (projectCarDetails.subscriptions) {
				replaceSubName(projectCarDetails.subscriptions);
			}

			if (projectCarDetails.prod && projectCarDetails.prod.subscriptions) {
				replaceSubName(projectCarDetails.prod.subscriptions);
			}

			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						...projectCarDetails
					}
				}
			};
		case 'updateNotificationSlideoutState':
			return {
				...state,
				notificationSlideout: { show: action.show }
			};
		case 'updateActiveChangeRequest':
			return {
				...state,
				activeChangeRequest: action.payload
			};
		case 'clearCurrentProjectDetails':
			return {
				...state,
				projects: {
					...state.projects,
					current: {
						...state.projects.current,
						devInfo: {},
						prodInfo: {}
					}
				}
			};
		case 'updateNotificationSettings':
			return {
				...state,
				notificationSettings: action.payload
			};
		case 'updateActiveProjectTab':
			return {
				...state,
				selectProjectTab: action.payload
			}
		case 'updatePreferenceSettings':
			return {
				...state,
				preferenceSettings: action.payload
			};
		case 'updateLoading':
			return {
				...state,
				loading: action.payload
			};
		case 'updateLoadings':
			return {
				...state,
				loadings: {
					...state.loadings,
					...action.payload
				}
			};
			case 'isLoadingSubscriptions':
			return {
				...state,
				loadings: {
					...state.loadings,
					...action.payload
				}
			};
		case 'showError':
			return {
				...state,
				error: {
					show: true,
					message: action.payload
				}
			};
		case 'showEnhancedError':
			return {
				...state,
				error: {
					show: true,
					message: action.payload.message,
					title: action.payload.title || '',
					points: action.payload.points || []
				}
			};
		case 'hideError':
			return {
				...state,
				error: {
					show: false,
					message: '',
					title: '',
					points: []
				}
			};
		case 'setExpiresAt':
			return {
				...state,
				expiresAt: action.payload
			};

		case 'partnerProjectSettings':
			return {
				...state,
				partnerProjectSettings: {
					filterAppOrPartner: action.payload.filterAppOrPartner,
					filterStatus: action.payload.filterStatus,
					filterTechAdvisor: action.payload.filterTechAdvisor,
					sortOrder: action.payload.sortOrder,
					pagination: action.payload.pagination
				}
			};

			case 'updateIntentDropdownSelection':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						intentDropdownSelection: action.payload
					}
				};
			case 'updateDomainDropdownSelection':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						apiDomainDropDownSelection: action.payload
					}
				};
			case 'updateSubCategoriesDropdownSelection':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						subCategoriesDropDownSelection: action.payload
					}
				};
			case 'updateDomainDropdownList':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						domainDropdownList: action.payload
					}
				};
	
			case 'updateSubCategoriesDropdownList':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						subCategoriesDropdownList: action.payload
					}
				};
			case 'updateSearchResponse':
				return {
					...state,
					apiDiscovery: {
						...state.apiDiscovery,
						searchResponse: action.payload
					}
				};
				case 'updateIsApiContext':
					return {
						...state,
						apiDiscovery: {
							...state.apiDiscovery,
							isApiContext: action.payload
						}
					};
			case 'upsertToasts':
				const upsertList = [...state.toasts.list];
				action.payload.toasts.forEach(({ key, ...rest }) => {
					const index = upsertList.findIndex(value => value.key === key);
					if (key && index >= 0) {
						const oldToast = upsertList[index];
						upsertList.splice(index, 1, { ...oldToast, ...rest });
					} else if (!action.payload.updateOnly) {
						const newKey = key || `random-toast-key-${uniqueId()}`;
						upsertList.push({ key: newKey, ...rest })
					}
				})
				return {
					...state,
					toasts: {
						...state.toasts,
						list: upsertList
					}
				};
			case 'removeToasts':
				const removeList = state.toasts.list.filter(({ key }) => action.payload.keys.findIndex(value => value === key) < 0);
				return {
					...state,
					toasts: {
						...state.toasts,
						list: removeList
					}
				};

		default:
			return state;
	}
};

export const ApplicationStateProvider = ({ children }) => (
	<ApplicationStateContext.Provider value={useReducer(reducer, initialState)}>
		{children}
	</ApplicationStateContext.Provider>
);

export const useApplicationState = () => useContext(ApplicationStateContext);

export const updateApplicationTitle = title => ({ type: 'changeTitle', title });
export const updateContactsList = list => ({ type: 'updateContactsList', list });
export const updateProjectsList = list => ({ type: 'updateProjectsList', list });
export const updateCurrentProject = project => ({ type: 'updateCurrentProject', project });
export const updateCurrentProjectDetails = details => ({ type: 'updateCurrentProjectDetails', details});
export const updateCurrentProjectDevSubscriptions = details => ({ type: 'updateCurrentProjectDevSubscriptions', details});
export const updateCurrentProjectProdSubscriptions = details => ({ type: 'updateCurrentProjectProdSubscriptions', details});
export const updateCurrentDevProjectDetails  = (details) => ({ type: 'updateCurrentDevProjectDetails', details });
export const updateCurrentProdProjectDetails  = (details) => ({ type: 'updateCurrentProdProjectDetails', details });
export const updateOrgNameInProjectDetailSubscriptions = (details) => ({ type: 'updateOrgNameInProjectDetailSubscriptions', details });
export const updateActiveChangeRequest = (payload) => ({ type: 'updateActiveChangeRequest', payload });


export const updateNotificationSlideoutState = show => ({ type: 'updateNotificationSlideoutState', show });
export const updateNotificationSettings = payload => ({ type: 'updateNotificationSettings', payload });
export const updatePreferenceSettings = payload => ({ type: 'updatePreferenceSettings', payload });
export const updateActiveProjectTab = payload => ({ type: 'updateActiveProjectTab', payload });

export const clearCurrentProjectDetails = () => ({ type: 'clearCurrentProjectDetails' });
// set loading indicator
export const setLoading = payload => ({ type: 'updateLoading', payload });
// set loading indicators
export const setLoadings = payload => ({ type: 'updateLoadings', payload });
export const setSubscriptionLoadings = payload => ({ type: 'isLoadingSubscriptions', payload });
// set error indicator
export const showErrorModal = payload => ({ type: 'showError', payload });
export const showEnhancedErrorModal = payload => ({ type: 'showEnhancedError', payload });
export const hideErrorModal = () => ({ type: 'hideError' });
// set expiresAt
export const setExpiresAt = payload => ({ type: 'setExpiresAt', payload });
export const updatePreventScrollOnRouteChange = prevent => ({ type: 'updatePreventScrollOnRouteChange', preventScrollOnRouteChange: prevent });
export const setPartnerProjectSettings = payload => ({ type: 'partnerProjectSettings', payload});

export const updateIntentDropdownSelection = payload => ({ type: 'updateIntentDropdownSelection', payload });
export const updateDomainDropdownSelection = payload => ({ type: 'updateDomainDropdownSelection', payload });
export const updateSubCategoriesDropdownSelection = payload => ({ type: 'updateSubCategoriesDropdownSelection', payload });

export const updateDomainDropdownList = payload => ({ type: 'updateDomainDropdownList', payload });
export const updateSubCategoriesDropdownList = payload => ({ type: 'updateSubCategoriesDropdownList', payload });
export const updateSearchResponse = payload => ({ type: 'updateSearchResponse', payload });

export const updateIsApiContext = payload => ({type: 'updateIsApiContext', payload});

/**
 *
 * @typedef {{
 * 	message?: string,
* 	messageClassName?: string,
* 	onMessageClose?: () => unknown,
* 	buttonOnClick?: () => unknown,
* 	buttonText?: string,
*   buttonBusy?: boolean,
* 	buttonDisabled?: boolean,
* 	autoCloseAfter?: number,
* 	key?: string
* }} Toast
 * @param {Toast | Array<Toast>} toasts
 * @param {boolean} updateOnly
 * @returns
 */
export const upsertToasts = (toasts, updateOnly) => {
	if (!Array.isArray(toasts)) toasts = [toasts];
	return { type: 'upsertToasts', payload: { toasts, updateOnly } };
};

/**
 *
 * @param {string | Array<string>} keys
 * @returns
 */
export const removeToasts = (keys) => {
	if (!Array.isArray(keys)) keys = [keys];
	return { type: 'removeToasts', payload: { keys } };
};
